import { Alert, Card, Col, Flex, InputNumber, Row, Slider } from 'antd';
import { Select } from 'antd/lib';
import { Text } from 'components/mvp-typography';
import { PrintableRootPage } from 'components/page/page-2';
import { formatISO } from 'date-fns';
import {
  useGetCGSInfo,
  useGetDHIInfo,
  useGetImPACTInfo,
  useGetPCSInfo,
  useGetPromisInfo
} from 'lib/hooks/tanstack/use-getInventoryInfo';
import { halfCol } from 'lib/utils/antd';
import { sortBy } from 'lodash';
import { useEffect, useMemo, useReducer } from 'react';
import { useParams } from 'react-router-dom';
import { InventoryTableDisplayInventoryFilter } from './components/inventory-table-display/types';
import { filterReducer } from './utils/filterReducer';
import {
  Assessment,
  AssessmentBreakdown
} from './components/assessment-breakdown';
import { ToggleWithLabel } from 'features/patient/_components/symptom-card/components/toggle-with-label';

function useGetAssessmentsForPatient(patientId: string) {
  const cgs = useGetCGSInfo(patientId);
  const dhi = useGetDHIInfo(patientId);
  const pcs = useGetPCSInfo(patientId);
  const promis = useGetPromisInfo(patientId);
  const impact = useGetImPACTInfo(patientId);

  const assessments = useMemo(() => {
    if (
      cgs.data === undefined ||
      dhi.data === undefined ||
      pcs.data === undefined ||
      promis.data === undefined ||
      impact.data === undefined
    )
      return [];

    return sortBy(
      cgs.data?.map<Assessment>((_, i) => ({
        date: _.date,
        cgs: _,
        dhi: dhi.data[i],
        pcs: pcs.data[i],
        promis: promis.data[i],
        impact: impact.data[i]
      })),
      _ => _.date.toDate().getTime()
    );
  }, [cgs.data, dhi.data, pcs.data, promis.data, impact.data]);

  return assessments;
}

export interface AggregatedFilterState {
  assessmentDate?: string;
  inventoryFilterConfig: InventoryTableDisplayInventoryFilter;
}

function useFilteredAssessmentsForPatient(
  filter: AggregatedFilterState,
  assessmentData: ReturnType<typeof useGetAssessmentsForPatient>
) {
  return assessmentData.find(
    _ =>
      formatISO(_.date.toDate(), { representation: 'date' }) ===
      filter.assessmentDate
  );
}

/**
 * List all the assessments and inventories of a Patient
 *
 * An assessment has: CGS, PCS, DHI, Promis, ImPACT
 */
export function InventoryAnalysisPage() {
  const { patientId } = useParams<{ patientId: string }>();
  const assessmentData = useGetAssessmentsForPatient(patientId);
  const [filter, setFilter] = useReducer(filterReducer, {
    inventoryFilterConfig: {
      criticalItemConfig: { enabled: false, threshold: 0.5 }
    },
    assessmentDate: ''
  });

  const filteredAssessment = useFilteredAssessmentsForPatient(
    filter,
    assessmentData
  );

  useEffect(() => {
    /**
     * Case 1: The array is empty
     *
     * Unset the selected value
     */
    if (assessmentData.length === 0) {
      setFilter({
        type: 'set-assessment-date',
        assessmentDate: ''
      });
      return;
    }
    /**
     * Case 2: A value is already selected
     *
     * Do not take any action
     */
    if (filter.assessmentDate !== '') {
      return;
    }

    /**
     * Case 3: The page has loaded for the first time
     *
     * Set the selected value to the last element
     */
    setFilter({
      type: 'set-assessment-date',
      assessmentDate: formatISO(assessmentData.at(-1)!.date.toDate(), {
        representation: 'date'
      })
    });
  }, [filter.assessmentDate, assessmentData]);

  return (
    <PrintableRootPage title="Inventory/Item analysis">
      <Flex vertical gap={24}>
        <Card>
          <Text style={{ fontWeight: 'bold' }}>Filters</Text>
          <Row gutter={24}>
            <Col {...halfCol}>
              <Text style={{ fontWeight: 'bold' }}>Assessment filters</Text>
              <Flex vertical>
                <Text>Select an assessment</Text>
                <Select
                  style={{ width: '100%' }}
                  value={filter.assessmentDate}
                  onSelect={(value: string) =>
                    setFilter({
                      type: 'set-assessment-date',
                      assessmentDate: value
                    })
                  }
                >
                  {assessmentData.map((_, i) => (
                    <Select.Option
                      key={_.date.toISOString()}
                      value={formatISO(_.date.toDate(), {
                        representation: 'date'
                      })}
                    >
                      Assessment {i + 1} -{' '}
                      {formatISO(_.date.toDate(), { representation: 'date' })}
                    </Select.Option>
                  ))}
                </Select>
              </Flex>
            </Col>
            <Col {...halfCol}>
              {' '}
              <div>
                <Text style={{ fontWeight: 'bold' }}>Item filters</Text>
                <ToggleWithLabel
                  label={'Only show critical items'}
                  customStyles={{
                    textStyle: {
                      fontSize: 'initial'
                    }
                  }}
                  checked={
                    filter.inventoryFilterConfig.criticalItemConfig.enabled
                  }
                  setChecked={() =>
                    setFilter({
                      type: 'set-critical-items-enabled',
                      enabled:
                        !filter.inventoryFilterConfig.criticalItemConfig.enabled
                    })
                  }
                />

                <Text>Critical item threshold.</Text>
                <Flex
                  style={{
                    width: '100%'
                  }}
                >
                  <Slider
                    style={{
                      flex: 1
                    }}
                    min={0}
                    max={1}
                    value={
                      filter.inventoryFilterConfig.criticalItemConfig.threshold
                    }
                    step={0.01}
                    onChange={value =>
                      setFilter({
                        type: 'set-critical-items-threshold',
                        threshold: value
                      })
                    }
                  />
                  <InputNumber
                    min={0}
                    max={1}
                    value={
                      filter.inventoryFilterConfig.criticalItemConfig.threshold
                    }
                    step={0.01}
                    onChange={value =>
                      value &&
                      setFilter({
                        type: 'set-critical-items-threshold',
                        threshold: value
                      })
                    }
                  />
                </Flex>
              </div>
            </Col>
          </Row>
        </Card>

        {filteredAssessment ? (
          <AssessmentBreakdown
            assessment={filteredAssessment}
            inventoryFilterConfig={filter.inventoryFilterConfig}
          />
        ) : (
          <Alert
            message="Please select an assessment to view the inventory breakdown."
            type="info"
          />
        )}
      </Flex>
    </PrintableRootPage>
  );
}
